/**
 * clipboardPasteFallback — makes Cmd/Ctrl+V work in a text field when the WebView's native paste
 * doesn't.
 *
 * WHY IT EXISTS. WKWebView's native paste can fail when its text input system isn't fully
 * activated: the keystroke reaches the page, no `paste` event is dispatched, and nothing is
 * inserted. This reads the Clipboard API and inserts the text by hand.
 *
 * WHY IT IS GLOBAL. It used to live inside useKeyboardShortcuts, whose sole call site is
 * TimelineView — so it existed on the Timeline route and nowhere else. Every other surface in the
 * app (the board and its Enhance dialog, the Script tab, Screenplay Manager, Projects, Sign In)
 * had no paste assistance at all, in the same WebView and the same page load. Installed once from
 * main.tsx, it now covers every route. It must be installed EXACTLY once: SceneBoardPage mounts
 * TimelineView on its Timeline tab, so a second copy left behind in the hook would fire alongside
 * this one and paste twice on that tab.
 *
 * WHY IT WAITS. Native paste works on some of our platforms and not others — macOS Standalone
 * forwards Cmd+V into the WKWebView itself (NativeWebViewMac's performKeyEquivalent), and every
 * ordinary browser pastes natively for the standalone/online-review modes. Inserting
 * unconditionally would DOUBLE-paste there, so the manual insert only runs when no `paste` event
 * arrived for that keystroke. Deferring the clipboard READ behind the same window matters too: a
 * read on every keystroke would raise Chrome's clipboard-permission prompt and Safari's paste
 * confirmation on the platforms that never needed the fallback in the first place. The grace
 * window stays well inside the ~5s transient-activation window, so the deferred read is still
 * covered by the user gesture.
 *
 * Never preventDefault on the paste arm: the native paste has to get its chance first, or the
 * fallback would be replacing a working path rather than backing up a broken one.
 *
 * WHY CMD+A IS HERE TOO. Same WebView, same root cause, different symptom. NativeWebViewMac's
 * performKeyEquivalent ends up in `[self keyDown:event]`, which fires a JS keydown WITHOUT running
 * the native `selectAll:` editing command — so Cmd+A in a text field silently does nothing in
 * macOS Standalone, and unlike paste there was no safety net for it anywhere in the app. Unlike
 * paste, select-all is IDEMPOTENT: on the platforms where the native command already works,
 * select() lands on the identical end state rather than doubling anything. So this arm needs none
 * of the grace-window machinery above — it runs, and preventDefaults, unconditionally.
 */

/** Wait this long for the native `paste` event before assuming it is never coming. The event is
 *  dispatched as the keydown's default action, so this only has to outlast WKWebView's async text
 *  input hop — and on the broken path it is the whole delay the user sees.
 *
 *  MUST stay under 1000ms, and the timer MUST stay installed directly from the keydown listener:
 *  WebKit forwards the user-gesture token into a timer only at nesting level 1 and only for delays
 *  under a second (DOMTimer's shouldForwardUserGesture). The deferred `readText()` below is gated
 *  on that token — raise this past a second, or nest the timer inside another one, and the read
 *  silently loses its gesture on the one engine the fallback exists for. */
const PASTE_GRACE_MS = 120;

/** Monotonic id for the keystroke currently in its grace window, and the id of the last one whose
 *  native paste actually fired. A `paste` with no preceding chord (right-click, iOS callout) just
 *  re-marks an already-resolved id — harmless. */
let pendingSeq = 0;
let nativePasteSeq = -1;

/** `navigator.clipboard` is absent on `file://` in WKWebView (ShareDialog.tsx:559), and `readText()`
 *  then throws a TypeError BEFORE any promise exists — a trailing `.catch()` never sees it. Returns
 *  null instead so the fallback degrades to silence. */
function readClipboardText(): Promise<string> | null {
  try {
    const clip = navigator.clipboard;
    if (!clip || typeof clip.readText !== 'function') return null;
    return clip.readText();
  } catch {
    return null;
  }
}

function insertText(el: HTMLTextAreaElement | HTMLInputElement, text: string): void {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  // execCommand for a React-compatible controlled-input update (it fires a real input event)
  document.execCommand('insertText', false, text);
  // Fallback: if execCommand didn't fire onChange, set value directly
  if (el.selectionStart === start && el.selectionEnd === end) {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
      'value',
    )?.set;
    if (nativeInputValueSetter) {
      const before = el.value.slice(0, start);
      const after = el.value.slice(end);
      nativeInputValueSetter.call(el, before + text + after);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

const onKeyDown = (e: KeyboardEvent): void => {
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey) return;
  const el = e.target;
  if (!(el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement)) return;

  // Select All. Idempotent, so no grace window and no native-command sniffing — see the header.
  if (e.code === 'KeyA') {
    el.select();
    e.preventDefault();
    return;
  }

  if (e.code !== 'KeyV') return;

  const seq = ++pendingSeq;
  const valueAtPress = el.value;

  window.setTimeout(() => {
    if (nativePasteSeq === seq) return; // native paste fired — inserting now would double it
    if (e.defaultPrevented) return; // someone cancelled this paste deliberately
    if (document.activeElement !== el) return; // focus moved; execCommand would land elsewhere
    if (el.value !== valueAtPress) return; // text already arrived, just without a paste event
    const read = readClipboardText();
    if (!read) return;
    read.then(text => { if (text) insertText(el, text); }).catch(() => { /* denied — nothing to do */ });
  }, PASTE_GRACE_MS);
};

const onNativePaste = (): void => { nativePasteSeq = pendingSeq; };

/** Installs the fallback on `window`, CAPTURE phase on both events: React attaches its listeners at
 *  the root container, so any component calling stopPropagation() would hide a bubble-phase window
 *  listener from us. Returns an uninstall function — used by the tests; production never
 *  uninstalls. */
export function installClipboardPasteFallback(): () => void {
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('paste', onNativePaste, true);
  return () => {
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('paste', onNativePaste, true);
  };
}
